import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, PauseCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { patientsApi } from '@/api/endpoints/patients.api';
import type { PatientMedication } from '@/types/patient.types';

const suspendSchema = z.object({
  reason: z.string().min(1, 'Motivo da suspensão é obrigatório').max(500, 'Motivo deve ter no máximo 500 caracteres'),
});

type SuspendFormData = z.infer<typeof suspendSchema>;

interface SuspendMedicationDialogProps {
  patientId: string;
  medication: PatientMedication;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (medication: PatientMedication) => void;
}

export function SuspendMedicationDialog({
  patientId,
  medication,
  open,
  onOpenChange,
  onSuccess,
}: SuspendMedicationDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SuspendFormData>({
    resolver: zodResolver(suspendSchema),
    defaultValues: {
      reason: '',
    },
  });

  const onSubmit = async (data: SuspendFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const updatedMedication = await patientsApi.suspendMedication(patientId, medication.id, {
        reason: data.reason,
      });
      onSuccess(updatedMedication);
      reset();
      onOpenChange(false);
    } catch (err: unknown) {
      console.error('Erro ao suspender medicamento:', err);

      // Verifica se é erro de permissão (403 Forbidden)
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 403) {
        setError('Você não tem permissão para suspender medicamentos. Esta ação é restrita a médicos.');
      } else {
        setError('Erro ao suspender medicamento. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PauseCircle className="h-5 w-5 text-amber-500" />
            Suspender Medicamento
          </DialogTitle>
          <DialogDescription>
            Você está prestes a suspender o medicamento <strong>{medication.medicationName}</strong>.
            Por favor, informe o motivo da suspensão.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <strong>Atenção:</strong> A suspensão do medicamento será registrada no histórico do paciente,
            incluindo a data, o motivo e o profissional responsável.
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Motivo da Suspensão *</Label>
            <Textarea
              id="reason"
              placeholder="Descreva o motivo da suspensão do medicamento..."
              rows={4}
              {...register('reason')}
            />
            {errors.reason && (
              <p className="text-sm text-destructive">{errors.reason.message}</p>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Suspender
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
